import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { TaskReadModel } from '../api/models'
import { decodeRouteToken, taskPath } from '../observability/routing'
import { useConsoleView } from '../observability/state'

function recordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
}

function textValue(value: unknown): string {
  if (value === undefined || value === null) return '—'
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function detailRecords(task: TaskReadModel, key: string): Record<string, unknown>[] {
  return recordArray(task.details?.[key])
}

export function Tasks() {
  const { snapshot } = useConsoleView()
  const [showDone, setShowDone] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [lane, setLane] = useState('all')
  const [status, setStatus] = useState('all')

  const filtered = useMemo(() => snapshot.tasks.filter((task) => {
    if (!showDone && task.state === 'done') return false
    if (!showArchived && task.archived) return false
    if (lane !== 'all' && task.lane !== lane) return false
    if (status !== 'all' && task.operationalStatus !== status) return false
    return true
  }), [snapshot.tasks, showDone, showArchived, lane, status])

  const ready = snapshot.tasks.filter((task) => task.operationalStatus === 'ready').length
  const inProgress = snapshot.tasks.filter((task) => task.operationalStatus === 'in_progress').length
  const blocked = snapshot.tasks.filter((task) => task.operationalStatus === 'blocked').length

  return (
    <section className="stack" aria-labelledby="tasks-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Backlog</p>
          <h2 id="tasks-title">Tasks</h2>
          <p className="muted">Lane, state, pressure and durable task history for this server.</p>
        </div>
        <span className="count-badge">{filtered.length}</span>
      </div>

      <div className="metric-grid">
        <article className="card"><span>Ready</span><strong>{ready}</strong></article>
        <article className="card"><span>In progress</span><strong>{inProgress}</strong></article>
        <article className="card"><span>Blocked</span><strong>{blocked}</strong></article>
      </div>

      <div className="filter-bar" aria-label="Task filters">
        <label>
          Lane
          <select value={lane} onChange={(event) => setLane(event.target.value)}>
            <option value="all">All lanes</option>
            <option value="implementation">Implementation</option>
            <option value="review">Review</option>
            <option value="integration">Integration</option>
            <option value="release">Release</option>
            <option value="general">General</option>
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="all">All statuses</option>
            <option value="ready">Ready</option>
            <option value="in_progress">In progress</option>
            <option value="blocked">Blocked</option>
            <option value="deferred">Deferred</option>
            <option value="done">Done</option>
          </select>
        </label>
        <label className="toggle-label"><input type="checkbox" checked={showDone} onChange={(event) => setShowDone(event.target.checked)} /> Show done</label>
        <label className="toggle-label"><input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} /> Show archived</label>
      </div>

      <div className="task-table" role="list">
        {filtered.map((task) => (
          <article className="task-row" role="listitem" key={task.key}>
            <div className="task-primary">
              <div className="chip-row">
                <span className="chip">{task.priority}</span>
                <span className="chip">{task.lane}</span>
                <span className={`status status-${task.operationalStatus === 'in_progress' ? 'live' : task.operationalStatus}`}>{task.operationalStatus}</span>
              </div>
              <Link className="task-title" to={taskPath(snapshot.instance.publicBaseUrl, task.namespace, task.taskId)}>
                {task.taskId} · {task.title}
              </Link>
              <p className="muted">{task.nextAction || 'No next action recorded.'}</p>
            </div>
            <div className="task-owner">
              <span className="muted">Owner</span>
              <strong>{task.owner?.agentName ?? 'unclaimed'}</strong>
            </div>
          </article>
        ))}
        {filtered.length === 0 && <div className="panel"><p className="muted">No tasks match these filters.</p></div>}
      </div>
    </section>
  )
}

export function TaskDetail() {
  const { snapshot } = useConsoleView()
  const params = useParams()
  const namespace = decodeRouteToken(params.namespace)
  const taskId = decodeRouteToken(params.taskId)
  const task = snapshot.tasks.find((item) => item.namespace === namespace && item.taskId === taskId)

  if (!task) {
    return <section className="panel"><h2>Task not found</h2><p className="muted">{namespace}/{taskId}</p></section>
  }

  const claims = detailRecords(task, 'claims')
  const reviews = detailRecords(task, 'reviews')
  const events = detailRecords(task, 'events')

  return (
    <section className="stack" aria-labelledby="task-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">{task.namespace}</p>
          <h2 id="task-title">{task.taskId} · {task.title}</h2>
          <p className="muted">{task.nextAction || 'No next action recorded.'}</p>
        </div>
        <span className={`status status-${task.operationalStatus === 'in_progress' ? 'live' : task.operationalStatus}`}>{task.operationalStatus}</span>
      </div>

      <div className="two-column">
        <article className="panel">
          <h3>Workflow</h3>
          <dl className="detail-list">
            <div><dt>Priority</dt><dd>{task.priority}</dd></div>
            <div><dt>Lane</dt><dd>{task.lane}</dd></div>
            <div><dt>State</dt><dd>{task.state}</dd></div>
            <div><dt>Archived</dt><dd>{task.archived ? 'yes' : 'no'}</dd></div>
            <div><dt>Candidate</dt><dd className="mono">{task.candidateRef ?? '—'}</dd></div>
          </dl>
        </article>

        <article className="panel">
          <h3>Claim</h3>
          {task.owner ? (
            <dl className="detail-list">
              <div><dt>Owner</dt><dd>{task.owner.agentName}</dd></div>
              <div><dt>Role</dt><dd>{task.owner.role}</dd></div>
              <div><dt>Intent</dt><dd>{task.owner.claimIntent}</dd></div>
              <div><dt>Age</dt><dd>{task.owner.claimAgeSeconds}s</dd></div>
            </dl>
          ) : <p className="muted">Unclaimed.</p>}
        </article>
      </div>

      <article className="panel">
        <h3>Evidence</h3>
        <div className="chip-row">{task.tags.map((tag) => <span className="chip" key={tag}>{tag}</span>)}</div>
        <details>
          <summary>Checkpoint</summary>
          <pre>{JSON.stringify(task.checkpoint, null, 2)}</pre>
        </details>
        {task.result !== undefined && (
          <details>
            <summary>Result</summary>
            <pre>{JSON.stringify(task.result, null, 2)}</pre>
          </details>
        )}
      </article>

      <div className="two-column">
        <article className="panel">
          <h3>Claims & reviews</h3>
          <div className="feed">
            {claims.map((claim, index) => (
              <div className="feed-row" key={`claim-${index}`}>
                <div><strong>{textValue(claim.agent_name ?? claim.agentName)}</strong><p className="muted">{textValue(claim.claim_intent ?? claim.claimIntent)}</p></div>
                <span className="chip">{textValue(claim.role)}</span>
              </div>
            ))}
            {reviews.map((review, index) => (
              <div className="feed-row" key={`review-${index}`}>
                <div><strong>{textValue(review.reviewer ?? review.agent_name ?? 'review')}</strong><p className="muted">{textValue(review.note ?? review.state)}</p></div>
                <span className="chip">{textValue(review.state)}</span>
              </div>
            ))}
            {claims.length + reviews.length === 0 && <p className="muted">No detailed claim/review history in this snapshot.</p>}
          </div>
        </article>

        <article className="panel">
          <h3>History</h3>
          <div className="timeline">
            {events.map((event, index) => (
              <div className="timeline-row" key={`event-${index}`}>
                <span className="timeline-dot" aria-hidden="true" />
                <div>
                  <strong>{textValue(event.event_type ?? event.type)}</strong>
                  <p className="muted">{textValue(event.created_at ?? event.createdAt)} · {textValue(event.agent_name ?? event.agentName)}</p>
                </div>
              </div>
            ))}
            {events.length === 0 && <p className="muted">No detailed task events in this snapshot.</p>}
          </div>
        </article>
      </div>
    </section>
  )
}
